/**
 * Do not edit manually.
 * 피처링 Open API
 * OpenAPI spec version: V1
 */
import { useInfiniteQuery, useQuery } from 'react-query';
import type {
	QueryFunction,
	QueryKey,
	UseInfiniteQueryOptions,
	UseInfiniteQueryResult,
	UseQueryOptions,
	UseQueryResult,
} from 'react-query';
import type {
	IGReportRes,
	NBReportRes,
	TTReportRes,
	TWReportRes,
	YTReportRes,
} from '../../model';
import { customInstance } from '../../customInstance';
import type { ErrorType } from '../../customInstance';
import { mutator } from '../../queryOptions';

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * 인스타그램 리포트 조회
 * @summary IgReportGet
 */
export const igReportGet = (
	platformPk: string,
	options?: SecondParameter<typeof customInstance>,
	signal?: AbortSignal,
) => {
	return customInstance<IGReportRes>(
		{ url: `/report/v2/ig/${platformPk}/`, method: 'GET', signal },
		options,
	);
};

export const getIgReportGetQueryKey = (platformPk: string) => {
	return [`/report/v2/ig/${platformPk}/`] as const;
};

export const useIgReportGetInfiniteQueryOptions = <
	TData = Awaited<ReturnType<typeof igReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseInfiniteQueryOptions<
			Awaited<ReturnType<typeof igReportGet>>,
			TError,
			TData
		>;
		request?: SecondParameter<typeof customInstance>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getIgReportGetQueryKey(platformPk);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof igReportGet>>> = ({ signal }) =>
		igReportGet(platformPk, requestOptions, signal);

	const customOptions = mutator({ ...queryOptions, queryKey, queryFn });

	return customOptions as UseInfiniteQueryOptions<
		Awaited<ReturnType<typeof igReportGet>>,
		TError,
		TData
	> & { queryKey: QueryKey };
};

export type IgReportGetInfiniteQueryResult = NonNullable<
	Awaited<ReturnType<typeof igReportGet>>
>;
export type IgReportGetInfiniteQueryError = ErrorType<unknown>;

/**
 * @summary IgReportGet
 */

export function useIgReportGetInfinite<
	TData = Awaited<ReturnType<typeof igReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseInfiniteQueryOptions<
			Awaited<ReturnType<typeof igReportGet>>,
			TError,
			TData
		>;
		request?: SecondParameter<typeof customInstance>;
	},
): UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } {
	const queryOptions = useIgReportGetInfiniteQueryOptions(platformPk, options);

	const query = useInfiniteQuery(queryOptions) as UseInfiniteQueryResult<
		TData,
		TError
	> & { queryKey: QueryKey };

	query.queryKey = queryOptions.queryKey;

	return query;
}

export const useIgReportGetQueryOptions = <
	TData = Awaited<ReturnType<typeof igReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseQueryOptions<Awaited<ReturnType<typeof igReportGet>>, TError, TData>;
		request?: SecondParameter<typeof customInstance>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getIgReportGetQueryKey(platformPk);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof igReportGet>>> = ({ signal }) =>
		igReportGet(platformPk, requestOptions, signal);

	const customOptions = mutator({ ...queryOptions, queryKey, queryFn });

	return customOptions as UseQueryOptions<
		Awaited<ReturnType<typeof igReportGet>>,
		TError,
		TData
	> & { queryKey: QueryKey };
};

export type IgReportGetQueryResult = NonNullable<Awaited<ReturnType<typeof igReportGet>>>;
export type IgReportGetQueryError = ErrorType<unknown>;

/**
 * @summary IgReportGet
 */

export function useIgReportGet<
	TData = Awaited<ReturnType<typeof igReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseQueryOptions<Awaited<ReturnType<typeof igReportGet>>, TError, TData>;
		request?: SecondParameter<typeof customInstance>;
	},
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
	const queryOptions = useIgReportGetQueryOptions(platformPk, options);

	const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
		queryKey: QueryKey;
	};

	query.queryKey = queryOptions.queryKey;

	return query;
}

/**
 * 네이버 블로그 리포트 조회
 * @summary NbReportGet
 */
export const nbReportGet = (
	platformPk: string,
	options?: SecondParameter<typeof customInstance>,
	signal?: AbortSignal,
) => {
	return customInstance<NBReportRes>(
		{ url: `/report/v2/nb/${platformPk}/`, method: 'GET', signal },
		options,
	);
};

export const getNbReportGetQueryKey = (platformPk: string) => {
	return [`/report/v2/nb/${platformPk}/`] as const;
};

export const useNbReportGetInfiniteQueryOptions = <
	TData = Awaited<ReturnType<typeof nbReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseInfiniteQueryOptions<
			Awaited<ReturnType<typeof nbReportGet>>,
			TError,
			TData
		>;
		request?: SecondParameter<typeof customInstance>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getNbReportGetQueryKey(platformPk);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof nbReportGet>>> = ({ signal }) =>
		nbReportGet(platformPk, requestOptions, signal);

	const customOptions = mutator({ ...queryOptions, queryKey, queryFn });

	return customOptions as UseInfiniteQueryOptions<
		Awaited<ReturnType<typeof nbReportGet>>,
		TError,
		TData
	> & { queryKey: QueryKey };
};

export type NbReportGetInfiniteQueryResult = NonNullable<
	Awaited<ReturnType<typeof nbReportGet>>
>;
export type NbReportGetInfiniteQueryError = ErrorType<unknown>;

/**
 * @summary NbReportGet
 */

export function useNbReportGetInfinite<
	TData = Awaited<ReturnType<typeof nbReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseInfiniteQueryOptions<
			Awaited<ReturnType<typeof nbReportGet>>,
			TError,
			TData
		>;
		request?: SecondParameter<typeof customInstance>;
	},
): UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } {
	const queryOptions = useNbReportGetInfiniteQueryOptions(platformPk, options);

	const query = useInfiniteQuery(queryOptions) as UseInfiniteQueryResult<
		TData,
		TError
	> & { queryKey: QueryKey };

	query.queryKey = queryOptions.queryKey;

	return query;
}

export const useNbReportGetQueryOptions = <
	TData = Awaited<ReturnType<typeof nbReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseQueryOptions<Awaited<ReturnType<typeof nbReportGet>>, TError, TData>;
		request?: SecondParameter<typeof customInstance>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getNbReportGetQueryKey(platformPk);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof nbReportGet>>> = ({ signal }) =>
		nbReportGet(platformPk, requestOptions, signal);

	const customOptions = mutator({ ...queryOptions, queryKey, queryFn });

	return customOptions as UseQueryOptions<
		Awaited<ReturnType<typeof nbReportGet>>,
		TError,
		TData
	> & { queryKey: QueryKey };
};

export type NbReportGetQueryResult = NonNullable<Awaited<ReturnType<typeof nbReportGet>>>;
export type NbReportGetQueryError = ErrorType<unknown>;

/**
 * @summary NbReportGet
 */

export function useNbReportGet<
	TData = Awaited<ReturnType<typeof nbReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseQueryOptions<Awaited<ReturnType<typeof nbReportGet>>, TError, TData>;
		request?: SecondParameter<typeof customInstance>;
	},
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
	const queryOptions = useNbReportGetQueryOptions(platformPk, options);

	const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
		queryKey: QueryKey;
	};

	query.queryKey = queryOptions.queryKey;

	return query;
}

/**
 * 틱톡 리포트 조회
 * @summary TtReportGet
 */
export const ttReportGet = (
	platformPk: string,
	options?: SecondParameter<typeof customInstance>,
	signal?: AbortSignal,
) => {
	return customInstance<TTReportRes>(
		{ url: `/report/v2/tt/${platformPk}/`, method: 'GET', signal },
		options,
	);
};

export const getTtReportGetQueryKey = (platformPk: string) => {
	return [`/report/v2/tt/${platformPk}/`] as const;
};

export const useTtReportGetInfiniteQueryOptions = <
	TData = Awaited<ReturnType<typeof ttReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseInfiniteQueryOptions<
			Awaited<ReturnType<typeof ttReportGet>>,
			TError,
			TData
		>;
		request?: SecondParameter<typeof customInstance>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getTtReportGetQueryKey(platformPk);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof ttReportGet>>> = ({ signal }) =>
		ttReportGet(platformPk, requestOptions, signal);

	const customOptions = mutator({ ...queryOptions, queryKey, queryFn });

	return customOptions as UseInfiniteQueryOptions<
		Awaited<ReturnType<typeof ttReportGet>>,
		TError,
		TData
	> & { queryKey: QueryKey };
};

export type TtReportGetInfiniteQueryResult = NonNullable<
	Awaited<ReturnType<typeof ttReportGet>>
>;
export type TtReportGetInfiniteQueryError = ErrorType<unknown>;

/**
 * @summary TtReportGet
 */

export function useTtReportGetInfinite<
	TData = Awaited<ReturnType<typeof ttReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseInfiniteQueryOptions<
			Awaited<ReturnType<typeof ttReportGet>>,
			TError,
			TData
		>;
		request?: SecondParameter<typeof customInstance>;
	},
): UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } {
	const queryOptions = useTtReportGetInfiniteQueryOptions(platformPk, options);

	const query = useInfiniteQuery(queryOptions) as UseInfiniteQueryResult<
		TData,
		TError
	> & { queryKey: QueryKey };

	query.queryKey = queryOptions.queryKey;

	return query;
}

export const useTtReportGetQueryOptions = <
	TData = Awaited<ReturnType<typeof ttReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseQueryOptions<Awaited<ReturnType<typeof ttReportGet>>, TError, TData>;
		request?: SecondParameter<typeof customInstance>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getTtReportGetQueryKey(platformPk);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof ttReportGet>>> = ({ signal }) =>
		ttReportGet(platformPk, requestOptions, signal);

	const customOptions = mutator({ ...queryOptions, queryKey, queryFn });

	return customOptions as UseQueryOptions<
		Awaited<ReturnType<typeof ttReportGet>>,
		TError,
		TData
	> & { queryKey: QueryKey };
};

export type TtReportGetQueryResult = NonNullable<Awaited<ReturnType<typeof ttReportGet>>>;
export type TtReportGetQueryError = ErrorType<unknown>;

/**
 * @summary TtReportGet
 */

export function useTtReportGet<
	TData = Awaited<ReturnType<typeof ttReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseQueryOptions<Awaited<ReturnType<typeof ttReportGet>>, TError, TData>;
		request?: SecondParameter<typeof customInstance>;
	},
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
	const queryOptions = useTtReportGetQueryOptions(platformPk, options);

	const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
		queryKey: QueryKey;
	};

	query.queryKey = queryOptions.queryKey;

	return query;
}

/**
 * 트위터 리포트 조회
 * @summary TwReportGet
 */
export const twReportGet = (
	platformPk: string,
	options?: SecondParameter<typeof customInstance>,
	signal?: AbortSignal,
) => {
	return customInstance<TWReportRes>(
		{ url: `/report/v2/tw/${platformPk}/`, method: 'GET', signal },
		options,
	);
};

export const getTwReportGetQueryKey = (platformPk: string) => {
	return [`/report/v2/tw/${platformPk}/`] as const;
};

export const useTwReportGetInfiniteQueryOptions = <
	TData = Awaited<ReturnType<typeof twReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseInfiniteQueryOptions<
			Awaited<ReturnType<typeof twReportGet>>,
			TError,
			TData
		>;
		request?: SecondParameter<typeof customInstance>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getTwReportGetQueryKey(platformPk);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof twReportGet>>> = ({ signal }) =>
		twReportGet(platformPk, requestOptions, signal);

	const customOptions = mutator({ ...queryOptions, queryKey, queryFn });

	return customOptions as UseInfiniteQueryOptions<
		Awaited<ReturnType<typeof twReportGet>>,
		TError,
		TData
	> & { queryKey: QueryKey };
};

export type TwReportGetInfiniteQueryResult = NonNullable<
	Awaited<ReturnType<typeof twReportGet>>
>;
export type TwReportGetInfiniteQueryError = ErrorType<unknown>;

/**
 * @summary TwReportGet
 */

export function useTwReportGetInfinite<
	TData = Awaited<ReturnType<typeof twReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseInfiniteQueryOptions<
			Awaited<ReturnType<typeof twReportGet>>,
			TError,
			TData
		>;
		request?: SecondParameter<typeof customInstance>;
	},
): UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } {
	const queryOptions = useTwReportGetInfiniteQueryOptions(platformPk, options);

	const query = useInfiniteQuery(queryOptions) as UseInfiniteQueryResult<
		TData,
		TError
	> & { queryKey: QueryKey };

	query.queryKey = queryOptions.queryKey;

	return query;
}

export const useTwReportGetQueryOptions = <
	TData = Awaited<ReturnType<typeof twReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseQueryOptions<Awaited<ReturnType<typeof twReportGet>>, TError, TData>;
		request?: SecondParameter<typeof customInstance>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getTwReportGetQueryKey(platformPk);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof twReportGet>>> = ({ signal }) =>
		twReportGet(platformPk, requestOptions, signal);

	const customOptions = mutator({ ...queryOptions, queryKey, queryFn });

	return customOptions as UseQueryOptions<
		Awaited<ReturnType<typeof twReportGet>>,
		TError,
		TData
	> & { queryKey: QueryKey };
};

export type TwReportGetQueryResult = NonNullable<Awaited<ReturnType<typeof twReportGet>>>;
export type TwReportGetQueryError = ErrorType<unknown>;

/**
 * @summary TwReportGet
 */

export function useTwReportGet<
	TData = Awaited<ReturnType<typeof twReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseQueryOptions<Awaited<ReturnType<typeof twReportGet>>, TError, TData>;
		request?: SecondParameter<typeof customInstance>;
	},
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
	const queryOptions = useTwReportGetQueryOptions(platformPk, options);

	const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
		queryKey: QueryKey;
	};

	query.queryKey = queryOptions.queryKey;

	return query;
}

/**
 * 유튜브 리포트 조회
 * @summary YtReportGet
 */
export const ytReportGet = (
	platformPk: string,
	options?: SecondParameter<typeof customInstance>,
	signal?: AbortSignal,
) => {
	return customInstance<YTReportRes>(
		{ url: `/report/v2/yt/${platformPk}/`, method: 'GET', signal },
		options,
	);
};

export const getYtReportGetQueryKey = (platformPk: string) => {
	return [`/report/v2/yt/${platformPk}/`] as const;
};

export const useYtReportGetInfiniteQueryOptions = <
	TData = Awaited<ReturnType<typeof ytReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseInfiniteQueryOptions<
			Awaited<ReturnType<typeof ytReportGet>>,
			TError,
			TData
		>;
		request?: SecondParameter<typeof customInstance>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getYtReportGetQueryKey(platformPk);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof ytReportGet>>> = ({ signal }) =>
		ytReportGet(platformPk, requestOptions, signal);

	const customOptions = mutator({ ...queryOptions, queryKey, queryFn });

	return customOptions as UseInfiniteQueryOptions<
		Awaited<ReturnType<typeof ytReportGet>>,
		TError,
		TData
	> & { queryKey: QueryKey };
};

export type YtReportGetInfiniteQueryResult = NonNullable<
	Awaited<ReturnType<typeof ytReportGet>>
>;
export type YtReportGetInfiniteQueryError = ErrorType<unknown>;

/**
 * @summary YtReportGet
 */

export function useYtReportGetInfinite<
	TData = Awaited<ReturnType<typeof ytReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseInfiniteQueryOptions<
			Awaited<ReturnType<typeof ytReportGet>>,
			TError,
			TData
		>;
		request?: SecondParameter<typeof customInstance>;
	},
): UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } {
	const queryOptions = useYtReportGetInfiniteQueryOptions(platformPk, options);

	const query = useInfiniteQuery(queryOptions) as UseInfiniteQueryResult<
		TData,
		TError
	> & { queryKey: QueryKey };

	query.queryKey = queryOptions.queryKey;

	return query;
}

export const useYtReportGetQueryOptions = <
	TData = Awaited<ReturnType<typeof ytReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseQueryOptions<Awaited<ReturnType<typeof ytReportGet>>, TError, TData>;
		request?: SecondParameter<typeof customInstance>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getYtReportGetQueryKey(platformPk);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof ytReportGet>>> = ({ signal }) =>
		ytReportGet(platformPk, requestOptions, signal);

	const customOptions = mutator({ ...queryOptions, queryKey, queryFn });

	return customOptions as UseQueryOptions<
		Awaited<ReturnType<typeof ytReportGet>>,
		TError,
		TData
	> & { queryKey: QueryKey };
};

export type YtReportGetQueryResult = NonNullable<Awaited<ReturnType<typeof ytReportGet>>>;
export type YtReportGetQueryError = ErrorType<unknown>;

/**
 * @summary YtReportGet
 */

export function useYtReportGet<
	TData = Awaited<ReturnType<typeof ytReportGet>>,
	TError = ErrorType<unknown>,
>(
	platformPk: string,
	options?: {
		query?: UseQueryOptions<Awaited<ReturnType<typeof ytReportGet>>, TError, TData>;
		request?: SecondParameter<typeof customInstance>;
	},
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
	const queryOptions = useYtReportGetQueryOptions(platformPk, options);

	const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
		queryKey: QueryKey;
	};

	query.queryKey = queryOptions.queryKey;

	return query;
}
